/**
 * WORKFLOW CONFIGS — Defines the sequential step flow for each entity.
 * 
 * Each workflow has:
 * - steps: ordered array of status keys (happy path)
 * - cancelledStatus: exits from the flow (shown as separate action)
 * - rejectedStatus: optional rejection status
 * - statusMapName: reference to status-registry key
 */

import type { StatusRegistryKey } from './status-registry';

export interface WorkflowStep {
    status: string;
    /** Allow skipping directly to this step from earlier steps */
    allowSkipTo?: boolean;
}

export interface WorkflowConfig {
    /** Entity display name */
    label: string;
    /** Steps in sequential order (happy path) */
    steps: string[];
    /** Status key that represents cancellation (shown as separate red button) */
    cancelledStatus?: string;
    /** Status key that represents rejection */
    rejectedStatus?: string;
    /** Status key that represents holding/pausing temporarily */
    pausedStatus?: string;
    /** Status key for hard stops / on hold */
    onHoldStatus?: string;
    /** Status key in the happy path AFTER which cancellation is no longer allowed. If undefined, can cancel anytime before the last step. */
    cancelableUntil?: string;
    /** Whether to allow stepping back to the previous status */
    allowBack?: boolean;
    /** Key in STATUS_REGISTRY for looking up labels/colors */
    statusMapName: StatusRegistryKey;
    /** Thứ tự chuẩn đầy đủ: trạng thái hiện tại không có trong steps (VD quy trình lấy từ DB) được chèn vào đúng vị trí */
    stepOrder?: string[];
    /** Trạng thái phụ / cũ hiển thị trên bước tương ứng (VD IN_PROGRESS → IN_PRODUCTION) */
    statusAliases?: Record<string, string>;
}

// ══════════════════════════════════════════════
// WORKFLOW DEFINITIONS
// ══════════════════════════════════════════════

export const ORDER_WORKFLOW: WorkflowConfig = {
    label: 'Đơn hàng',
    // Đã lên KH (PLANNED): đơn đã có lệnh sản xuất, chưa chạy — sau Đã xác nhận, trước Đang SX (FB-JOU4AV)
    steps: ['DRAFT', 'PENDING', 'APPROVED', 'CONFIRMED', 'PLANNED', 'IN_PRODUCTION', 'COMPLETED', 'DELIVERED', 'INVOICED', 'CLOSED'],
    cancelledStatus: 'CANCELLED',
    cancelableUntil: 'CONFIRMED', // Cannot cancel after CONFIRMED
    allowBack: true,
    statusMapName: 'OrderStatus',
    statusAliases: { WAITING_PRE_PRESS: 'PENDING', IN_PROGRESS: 'IN_PRODUCTION' },
};

export const QUOTATION_WORKFLOW: WorkflowConfig = {
    label: 'Báo giá',
    steps: ['DRAFT', 'SENT', 'PROCESSING', 'ESTIMATED', 'APPROVED'],
    cancelledStatus: undefined,
    rejectedStatus: 'REJECTED',
    cancelableUntil: 'PROCESSING',
    allowBack: true,
    statusMapName: 'QuotationStatus',
};

export const PRODUCTION_ORDER_WORKFLOW: WorkflowConfig = {
    label: 'Lệnh SX',
    steps: ['DRAFT', 'PLANNED', 'RELEASED', 'IN_PROGRESS', 'COMPLETED'],
    cancelledStatus: 'CANCELLED',
    cancelableUntil: 'RELEASED',
    allowBack: true,
    statusMapName: 'ProductionOrderStatus',
    // Trạng thái trung gian (Chờ duyệt ban hành, Chế bản…) hiện đúng chỗ trên thanh khi lệnh đang ở đó (FB-EK0WTS)
    stepOrder: ['DRAFT', 'PENDING_APPROVAL', 'PRE_PRESS', 'PRE_PRESS_COMPLETED', 'READY_FOR_SCHEDULING', 'MATERIALS_READY', 'PLANNED', 'RELEASED', 'IN_PROGRESS', 'MANUFACTURED', 'COMPLETED', 'CLOSED'],
};

export const OUTSOURCE_PO_WORKFLOW: WorkflowConfig = {
    label: 'Đơn gia công',
    steps: ['DRAFT', 'PENDING', 'IN_PROGRESS', 'COMPLETED'],
    cancelledStatus: 'CANCELLED',
    cancelableUntil: 'PENDING',
    allowBack: true,
    statusMapName: 'OutsourcePOStatus',
};

export const PURCHASE_ORDER_WORKFLOW: WorkflowConfig = {
    label: 'Đơn mua hàng',
    steps: ['DRAFT', 'PENDING', 'APPROVED', 'ORDERED', 'PARTIAL_RECEIVED', 'RECEIVED'],
    cancelledStatus: 'CANCELLED',
    cancelableUntil: 'APPROVED',
    allowBack: true,
    statusMapName: 'PurchaseOrderStatus',
};

export const DELIVERY_WORKFLOW: WorkflowConfig = {
    label: 'Giao hàng',
    steps: ['DRAFT', 'SCHEDULED', 'IN_TRANSIT', 'DELIVERED'],
    cancelledStatus: 'CANCELLED',
    cancelableUntil: 'SCHEDULED',
    allowBack: true,
    statusMapName: 'DeliveryStatus',
};

// ══════════════════════════════════════════════
// TECH MODULE WORKFLOWS
// ══════════════════════════════════════════════

export const TECHNICAL_SPEC_WORKFLOW: WorkflowConfig = {
    label: 'Thông số kỹ thuật',
    steps: ['DRAFT', 'PENDING', 'APPROVED', 'OBSOLETE'],
    rejectedStatus: 'REJECTED',
    allowBack: true,
    statusMapName: 'ApprovalStatus',
};

export const MOLD_WORKFLOW: WorkflowConfig = {
    label: 'Khuôn bế',
    steps: ['DRAFT', 'PENDING', 'APPROVED'],
    rejectedStatus: 'REJECTED',
    allowBack: true,
    statusMapName: 'ApprovalStatus',
};

export const PLATE_WORKFLOW: WorkflowConfig = {
    label: 'Kẽm in',
    steps: ['DRAFT', 'PENDING', 'APPROVED'],
    rejectedStatus: 'REJECTED',
    allowBack: true,
    statusMapName: 'ApprovalStatus',
};

// ══════════════════════════════════════════════
// REGISTRY — lookup by entity name
// ══════════════════════════════════════════════


export const QC_PROCESS_WORKFLOW: WorkflowConfig = {
    label: "QC Công đoạn",
    steps: ["PENDING", "INSPECTING", "PASSED"],
    rejectedStatus: "FAILED",
    statusMapName: "QCStatus",
};

export const QC_FINAL_WORKFLOW: WorkflowConfig = {
    label: "QC Cuối",
    steps: ["PENDING", "INSPECTING", "PASSED"],
    rejectedStatus: "FAILED",
    statusMapName: "QCStatus",
};

export const WORKFLOW_REGISTRY: Record<string, WorkflowConfig> = {
    order: ORDER_WORKFLOW,
    quotation: QUOTATION_WORKFLOW,
    productionOrder: PRODUCTION_ORDER_WORKFLOW,
    outsourcePO: OUTSOURCE_PO_WORKFLOW,
    purchaseOrder: PURCHASE_ORDER_WORKFLOW,
    delivery: DELIVERY_WORKFLOW,
    technicalSpec: TECHNICAL_SPEC_WORKFLOW,
    mold: MOLD_WORKFLOW,
    plate: PLATE_WORKFLOW,
    qcProcess: QC_PROCESS_WORKFLOW,
    qcFinal: QC_FINAL_WORKFLOW,
};
